/**
 * Staff booking-request inbox - /schedule/booking-requests.
 *
 * Backed by the public online-booking flow: a visitor picks a slot on the
 * patient-facing site, verifies their contact info, and lands here as a
 * request. Providers see only their own requests; administrators see the
 * whole organization (the backend enforces the scoping, not this client).
 */
import { apiGet, apiPost } from "./client";

export type BookingRequestStatus =
  | "pending_verification"
  | "pending"
  | "confirmed"
  | "modified"
  | "declined"
  | "expired"
  | "cancelled";

export interface StaffBookingRequestDTO {
  id: string;
  provider_user_id: string;
  provider_name: string;
  service_name: string;
  facility_name?: string;
  modality: "in_office" | "telehealth";
  patient_type: "new" | "established";
  requested_start: string;
  requested_end: string;
  timezone: string;
  patient_first_name: string;
  patient_last_name: string;
  patient_dob?: string;
  patient_email?: string;
  patient_phone?: string;
  reason_summary?: string;
  status: BookingRequestStatus;
  matched_patient_id?: string;
  appointment_id?: string;
  /** "portal": sent by a signed-in patient, already linked to matched_patient_id.
   *  "app": sent from the Orange app by someone new to this clinic. */
  source?: "public" | "portal" | "app";
  /** No time suited them: call, then propose a time (it can't be confirmed as is). */
  callback_requested?: boolean;
  best_time_to_call?: string;
  /** Anything else they wanted the provider to know. */
  notes?: string;
  /** Portal reschedule: this earlier appointment is cancelled on confirm. */
  reschedule_of_appointment_id?: string;
  decline_reason?: string;
  /** Company (EAP) code the patient entered, resolved to the corporate client. */
  corporate_client_id?: string;
  company_name?: string;
  employee_id?: string;
  proposed_start?: string;
  proposed_end?: string;
  created_at: string;
  verified_at?: string;
}

export interface StaffPatientCandidateDTO {
  patient_id: string;
  mrn: string;
  first_name: string;
  last_name: string;
}

export function listBookingRequests(status?: string): Promise<StaffBookingRequestDTO[]> {
  const query = status ? `?status=${encodeURIComponent(status)}` : "";
  return apiGet<StaffBookingRequestDTO[]>(`/booking-requests${query}`);
}

export function getBookingRequest(id: string): Promise<StaffBookingRequestDTO> {
  return apiGet<StaffBookingRequestDTO>(`/booking-requests/${id}`);
}

export function getPatientCandidates(id: string): Promise<StaffPatientCandidateDTO[]> {
  return apiGet<StaffPatientCandidateDTO[]>(`/booking-requests/${id}/candidates`);
}

export function confirmBookingRequest(
  id: string,
  matchedPatientId?: string,
  skipCompany = false,
): Promise<StaffBookingRequestDTO> {
  return apiPost<StaffBookingRequestDTO>(`/booking-requests/${id}/confirm`, {
    matched_patient_id: matchedPatientId ?? "",
    skip_company: skipCompany,
  });
}

export function modifyBookingRequest(
  id: string,
  proposedStart: string,
  proposedEnd: string,
): Promise<StaffBookingRequestDTO> {
  return apiPost<StaffBookingRequestDTO>(`/booking-requests/${id}/modify`, {
    proposed_start: proposedStart,
    proposed_end: proposedEnd,
  });
}

export function declineBookingRequest(
  id: string,
  reasonCode: string,
): Promise<StaffBookingRequestDTO> {
  return apiPost<StaffBookingRequestDTO>(`/booking-requests/${id}/decline`, {
    reason_code: reasonCode,
  });
}
